import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable } from 'rxjs';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';
import { AutomationsRouterService } from '../../../../shared/automations-router.service';
import { MessageStatus } from '@vendasta/conversation';
import {
  SendEmailViaInboxTaskDefinitionId,
  SendEventViaInboxTaskDefinitionId,
  SendSMSViaInboxTaskDefinitionId,
} from '../../../component-loader/constants';

const NODE_TITLE: { [key: string]: string } = {
  [SendSMSViaInboxTaskDefinitionId]: 'AUTOMATIONS.EDITOR.TASKS.SEND_SMS_INBOX.TITLE',
  [SendEventViaInboxTaskDefinitionId]: 'AUTOMATIONS.EDITOR.TASKS.SEND_EVENT_INBOX.TITLE',
  [SendEmailViaInboxTaskDefinitionId]: 'AUTOMATIONS.EDITOR.TASKS.SEND_EMAIL_INBOX.TITLE',
};

@Injectable({ providedIn: 'root' })
export class SendInboxOnChannelService extends AutomationDisplayService {
  constructor(
    protected readonly translateService: TranslateService,
    private readonly router: AutomationsRouterService,
  ) {
    super();
  }

  /** Gets text for the activity column in the table. */
  getActivityColumn(_namespace: string, activity: ActivityInterface): ActivityTableEntry {
    const activityData = activity?.activityData ? JSON.parse(activity.activityData) : {};
    const statusMessage = this.translateService.instant(
      this.getTranslationKeyFromMessageStatus(activityData?.message_status_code),
    );
    return this.tableEntry(
      this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.SEND_SMS_INBOX.ACTIVITY', {
        message: activityData?.rendered_message,
      }),
      this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.SEND_SMS_INBOX.MESSAGE_STATUS', {
        status: statusMessage,
      }),
    );
  }

  getTranslationKeyFromMessageStatus(status: MessageStatus): string {
    switch (status) {
      case MessageStatus.MESSAGE_STATUS_NOT_READ:
        return 'AUTOMATIONS.EDITOR.TASKS.SEND_SMS_INBOX.DELIVERY_STATUS.STATUS_NOT_READ';
      case MessageStatus.MESSAGE_STATUS_READ:
        return 'AUTOMATIONS.EDITOR.TASKS.SEND_SMS_INBOX.DELIVERY_STATUS.STATUS_READ';
      case MessageStatus.MESSAGE_STATUS_SENT:
        return 'AUTOMATIONS.EDITOR.TASKS.SEND_SMS_INBOX.DELIVERY_STATUS.STATUS_SENT';
      case MessageStatus.MESSAGE_STATUS_DELIVERED:
        return 'AUTOMATIONS.EDITOR.TASKS.SEND_SMS_INBOX.DELIVERY_STATUS.STATUS_DELIVERED';
      case MessageStatus.MESSAGE_STATUS_FAILED:
        return 'AUTOMATIONS.EDITOR.TASKS.SEND_SMS_INBOX.DELIVERY_STATUS.STATUS_FAILED';
      case MessageStatus.MESSAGE_STATUS_UNDELIVERED:
        return 'AUTOMATIONS.EDITOR.TASKS.SEND_SMS_INBOX.DELIVERY_STATUS.STATUS_UNDELIVERED';
      case MessageStatus.MESSAGE_STATUS_SENDING:
        return 'AUTOMATIONS.EDITOR.TASKS.SEND_SMS_INBOX.DELIVERY_STATUS.STATUS_SENDING';
      default:
        return 'AUTOMATIONS.EDITOR.TASKS.SEND_SMS_INBOX.DELIVERY_STATUS.STATUS_UNKNOWN';
    }
  }

  /** The text in an automation node (i.e. the nodes in the graph). */
  getAutomationNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    const nodeTitle = NODE_TITLE?.[step.taskDefinitionId];
    return this.nodeDisplayData(this.translateService.stream(nodeTitle), this.noValidationErrorsDetected());
  }
}
